/**
 * A base tooltip component that handles positioning
 * Uses CSS-only hover detection via data attributes for reliability
 */
import React from 'react';

/**
 * BaseToolTip - Core tooltip component that handles positioning and visibility.
 * This component is not exported directly; use ToolTipBottom, ToolTipTop, ToolTipLeft, or ToolTipRight for specific placements.
 * @prop {React.ReactNode} children - The content to display inside the tooltip.
 * @prop {'bottom'|'top'|'left'|'right'} preferredPlacement - The preferred placement of the tooltip relative to the trigger element.
 * @prop {number} [width] - Optional max width of the tooltip in pixels; defaults to 300px if not provided.
 * @param {number} [yOffset] - Optional additional vertical offset in pixels to fine-tune tooltip position.
 * @param {number} [xOffset] - Optional additional horizontal offset in pixels to fine-tune tooltip position.
 * @param {boolean} [isVisible=false] - Controls whether the tooltip is visible; when false, the tooltip is hidden via CSS.
 * @return {JSX.Element} The rendered tooltip element, positioned according to the specified props.
 * 
 */

function BaseToolTip({ children, preferredPlacement = 'bottom', width, yOffset, xOffset, isVisible }) {
    const positionClasses = {
        bottom: 'bottom-0 translate-y-full left-1/2 -translate-x-1/2',
        top: 'top-0 -translate-y-full left-1/2 -translate-x-1/2',
        left: 'left-0 -translate-x-full top-1/2 -translate-y-1/2',
        right: 'right-0 translate-x-full top-1/2 -translate-y-1/2'
    };

    const dynamicStyles = {
        pointerEvents: 'none',
        whiteSpace: 'normal',
        maxWidth: width ? `${width}px` : '300px',
        width: width ? `${width}px` : 'max-content',
    };

    const defaultOffset = '8px';

    switch (preferredPlacement) {
        case 'bottom':
            dynamicStyles.marginTop = yOffset ? `${yOffset}px` : defaultOffset;
            break;
        case 'top':
            dynamicStyles.marginTop = yOffset ? `-${yOffset}px` : `-${defaultOffset}`;
            break;
        case 'left':
            dynamicStyles.marginLeft = xOffset ? `-${xOffset}px` : `-${defaultOffset}`;
            break;
        case 'right':
            dynamicStyles.marginLeft = xOffset ? `${xOffset}px` : defaultOffset;
            break;
        default:
            break;
    }

    return (
        <div className="relative" style={{ overflow: 'visible' }}>
            <div
                data-tooltip-content
                className={`
                    absolute
                    ${positionClasses[preferredPlacement]}
                    z-[9999]
                    bg-white
                    rounded-lg
                    border
                    border-gray-200
                    shadow-lg
                    ${isVisible ? '' : 'hidden'}
                `}
                style={dynamicStyles}
            >
                <div className="p-3">
                    <p className="text-sm text-gray-600">
                        {children}
                    </p>
                </div>
            </div>
        </div>
    );
}

/**
 * Tooltip that appears below the trigger element.
 * @param {Object} props
 * @param {React.ReactNode} props.children - Tooltip content; renders nothing when falsy.
 * @param {number} [props.width] - Max width in pixels.
 * @param {number} [props.yOffset] - Extra downward offset in pixels.
 * @returns {JSX.Element|null}
 */
export function ToolTipBottom({ children, width, yOffset, isVisible = false }) {
    if (!children) return null;
    return <BaseToolTip preferredPlacement="bottom" width={width} yOffset={yOffset} isVisible={isVisible}>{children}</BaseToolTip>;
}

/**
 * Tooltip that appears above the trigger element.
 * @param {Object} props
 * @param {React.ReactNode} props.children - Tooltip content; renders nothing when falsy.
 * @param {number} [props.width] - Max width in pixels.
 * @param {number} [props.yOffset] - Extra upward offset in pixels.
 * @returns {JSX.Element|null}
 */
export function ToolTipTop({ children, width, yOffset, isVisible = false }) {
    if (!children) return null;
    return <BaseToolTip preferredPlacement="top" width={width} yOffset={yOffset} isVisible={isVisible}>{children}</BaseToolTip>;
}

/**
 * Tooltip that appears to the left of the trigger element.
 * @param {Object} props
 * @param {React.ReactNode} props.children - Tooltip content; renders nothing when falsy.
 * @param {number} [props.width] - Max width in pixels.
 * @param {number} [props.xOffset] - Extra leftward offset in pixels.
 * @returns {JSX.Element|null}
 */
export function ToolTipLeft({ children, width, xOffset, isVisible = false }) {
    if (!children) return null;
    return <BaseToolTip preferredPlacement="left" width={width} xOffset={xOffset} isVisible={isVisible}>{children}</BaseToolTip>;
}

/**
 * Tooltip that appears to the right of the trigger element.
 * @param {Object} props
 * @param {React.ReactNode} props.children - Tooltip content; renders nothing when falsy.
 * @param {number} [props.width] - Max width in pixels.
 * @param {number} [props.xOffset] - Extra rightward offset in pixels.
 * @returns {JSX.Element|null}
 */
export function ToolTipRight({ children, width, xOffset, isVisible = false }) {
    if (!children) return null;
    return <BaseToolTip preferredPlacement="right" width={width} xOffset={xOffset} isVisible={isVisible}>{children}</BaseToolTip>;
}

/**
* Container component using CSS hover for reliable tooltip visibility.
 * Uses arbitrary variant [&:hover_[data-tooltip-content]] to show tooltips
 * on hover via pure CSS - no JS mouse events that can get stuck.
 *
 * @param {Object} props
 * @param {React.ReactNode} props.children - The trigger element and its tooltip sibling.
 * @returns {JSX.Element}
 
 */
export function ToolTipHoverContainer({ children }) {
    return (
        <div
            className="relative [&:hover_[data-tooltip-content]]:block"
            style={{ overflow: 'visible' }}
        >
            {children}
        </div>
    );
}
